import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { IconFileText, IconUpload, IconBrandYoutube, IconPresentation, IconQuestionMark } from "@tabler/icons-react";
import { LibraryBig, Share2, MoreVertical, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useJournal } from "@/hooks/useJournal";
import { useJournalBlocks, useCreateBlock, useUpdateBlock, useDeleteBlock, useReorderBlocks, useDebouncedBlockContentUpdate } from "@/hooks/useJournalBlocks";
import { useUpdateJournal, useDeleteJournal } from "@/hooks/useJournals";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { BlockType, TodoItem } from "@/types/journal";
import { SpaceTabBar, Tab } from "./SpaceTabBar";
import { YouTubeTabView } from "./YouTubeTabView";
import { PDFTabView } from "./PDFTabView";
import { SlideDeckTabView } from "./SlideDeckTabView";
import { SlideContent } from "./services/mockSlideDeckService";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SpaceEmptyState from "./SpaceEmptyState";
import { SpaceBlock } from "./SpaceBlock";
import { SortableBlockWrapper } from "./SortableBlockWrapper";
import { SpaceInsertPoint } from "./SpaceInsertPoint";
import { NoteBlock } from "./blocks/NoteBlock";
import { StreamingNoteBlock } from "./blocks/StreamingNoteBlock";
import { PDFBlock } from "./blocks/PDFBlock";
import { YouTubeBlock } from "./blocks/YouTubeBlock";
import { AIPromptBlock } from "./blocks/AIPromptBlock";
import { QuizBlock, QuizBlockState } from "./blocks/QuizBlock";
import { SlideDeckBlock, SlideDeckBlockContent } from "./blocks/SlideDeckBlock";
import { JournalTodoList } from "./JournalTodoList";
import { ShareDrawer } from "./ShareDrawer";
import { JournalTableOfContents, type TOCItem } from "./JournalTableOfContents";
import { JournalFloatingActions } from "./JournalFloatingActions";
import { useScrollSpy } from "@/hooks/useScrollSpy";
import type { Block as BlockNoteBlock } from "@blocknote/core";

// Import PDFBlockContent type
import { PDFBlockContent } from "./blocks/PDFBlock";
import { deletePDFFromStorage, getPDFSignedUrl } from "@/hooks/usePDFUpload";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useJournalSummaryUpdates } from "@/hooks/useJournalSummaryUpdates";
import { analyzeYouTube, analyzePDF, generateQuiz } from "@/services/journalAIService";
import AmbientWave, { type AmbientState } from "@/components/chat/AmbientWave";
import MorphingPill, { type MorphingPillProps } from "@/components/chat/MorphingPill";
import type { GeminiAudioManager } from "@/lib/gemini-audio";

// Re-export for compatibility
type PDFContent = PDFBlockContent;

interface YouTubeContent {
  videoUrl: string | null;
}

// SlideDeckContent is now SlideDeckBlockContent from the SlideDeckBlock component

// Note content can be BlockNote blocks or markdown from AI
type NoteContent = BlockNoteBlock[] | { markdown: string };

type BlockContent = string | PDFContent | YouTubeContent | QuizBlockState | SlideDeckBlockContent | NoteContent | null;

// Streaming note content
interface StreamingNoteContent {
  prompt: string;
  streamedText?: string;
}

// Helper to get default title for block types
function getDefaultTitle(type: string): string {
  switch (type) {
    case 'note': return 'Note';
    case 'streaming-note': return 'AI Note';
    case 'pdf': return 'PDF';
    case 'youtube': return 'Video';
    case 'slide-deck': return 'Explainer';
    case 'quiz': return 'Quiz';
    default: return 'Block';
  }
}

// Helper to extract preview text from note content for TOC
function extractNotePreviewText(content: unknown, maxLength = 30): string | null {
  if (!content) return null;

  // Helper to recursively extract text from BlockNote structure
  const extractText = (obj: unknown): string => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    if (Array.isArray(obj)) {
      return obj.map(extractText).join(' ').trim();
    }
    if (typeof obj === 'object') {
      const o = obj as Record<string, unknown>;
      // Check common content structures
      if (o.text && typeof o.text === 'string') return o.text;
      if (o.markdown && typeof o.markdown === 'string') return o.markdown;
      if (o.blocks) return extractText(o.blocks);
      if (o.content) return extractText(o.content);
      if (o.children) return extractText(o.children);
    }
    return '';
  };

  const text = extractText(content).trim();
  if (!text) return null;

  // Clean up and truncate
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (cleaned.length <= maxLength) return cleaned;
  return cleaned.slice(0, maxLength - 1).trim() + '…';
}

// Block types
interface Block {
  id: string;
  type: 'note' | 'pdf' | 'youtube' | 'quiz' | 'slide-deck' | 'streaming-note';
  title: string;
  content?: BlockContent | StreamingNoteContent;
  isExpanded: boolean;
  createdAt: Date;
}

interface SpaceCanvasSectionProps {
  // Journal ID
  journalId: string;
  // Vision integration callbacks for AI agent
  onCanvasViewportRef?: (getCanvasEl: () => HTMLElement | null) => void;
  onScreenStreamChange?: (stream: MediaStream | null) => void;
  // Tab state callbacks
  onTabsChange?: (hasContentTabs: boolean) => void;
  // Fullscreen toggle
  isFullscreen?: boolean;
  onToggleFullscreen?: () => void;
  // Ambient state for wave visualization
  ambientState?: AmbientState;
  audioManager?: GeminiAudioManager | null;
  // MorphingPill props for dock control
  pillProps?: Omit<MorphingPillProps, 'className'>;
}

const SpaceCanvasSection = ({
  journalId,
  onCanvasViewportRef,
  onScreenStreamChange,
  onTabsChange,
  isFullscreen = false,
  onToggleFullscreen,
  ambientState = 'disconnected',
  audioManager,
  pillProps,
}: SpaceCanvasSectionProps) => {
  const navigate = useNavigate();
  // Get current user for storage paths
  const { user } = useAuth();

  // Screen sharing state
  const [screenStream, setScreenStream] = useState<MediaStream | null>(null);

  // PDF upload states (keyed by block ID)
  const [pdfUploads, setPdfUploads] = useState<Record<string, {
    isUploading: boolean;
    progress: number;
    error?: string;
  }>>({});

  // Fetch journal and blocks from database
  const { data: journal } = useJournal(journalId);
  const { data: journalBlocks } = useJournalBlocks(journalId);
  const updateJournalMutation = useUpdateJournal();
  const deleteJournalMutation = useDeleteJournal();
  const createBlockMutation = useCreateBlock();
  const updateBlockMutation = useUpdateBlock();
  const deleteBlockMutation = useDeleteBlock();
  const reorderBlocksMutation = useReorderBlocks();
  const debouncedContentUpdate = useDebouncedBlockContentUpdate(journalId);

  // Smart journal summary updates
  const summaryUpdates = useJournalSummaryUpdates(journalId);

  // Journal name state (synced with database)
  const [journalName, setJournalName] = useState('Untitled Journal');
  const [isEditingName, setIsEditingName] = useState(false);

  // Local blocks state (synced with database blocks)
  const [blocks, setBlocks] = useState<Block[]>([]);

  // Share banner visibility state
  const [isShareBannerOpen, setIsShareBannerOpen] = useState(false);

  // Todo expanded state tracking (for collapse all)
  const [isTodoExpanded, setIsTodoExpanded] = useState(true);
  const [forceTodoCollapse, setForceTodoCollapse] = useState(false);

  // Scroll container ref for back to top functionality
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Build TOC items from blocks (memoized)
  // Todo is always first, then blocks with extracted titles for notes
  const tocItems: TOCItem[] = useMemo(() => {
    // Start with Todo as the first item (always present)
    const items: TOCItem[] = [{
      id: 'todo-list',
      title: 'To do',
      type: 'todo',
    }];

    // Add blocks with proper title extraction
    blocks.forEach(block => {
      let title = block.title;

      // For notes, extract text from content if title is empty/default
      if ((block.type === 'note' || block.type === 'streaming-note') && (!title || title === 'New Note' || title === 'Note')) {
        title = extractNotePreviewText(block.content) || getDefaultTitle(block.type);
      }

      items.push({
        id: block.id,
        title: title || getDefaultTitle(block.type),
        type: block.type,
      });
    });

    return items;
  }, [blocks]);

  // Scroll spy for active block tracking
  const activeBlockId = useScrollSpy(
    tocItems.map(item => item.id),
    { rootMargin: '-20% 0px -60% 0px' }
  );

  // Handle TOC item click - scroll to block
  const handleTOCItemClick = useCallback((blockId: string) => {
    const element = document.querySelector(`[data-toc-id="${blockId}"]`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  // Calculate expanded block count (for collapse all visibility)
  const expandedBlockCount = useMemo(() => {
    const expandedBlocks = blocks.filter(b => b.isExpanded).length;
    return expandedBlocks + (isTodoExpanded ? 1 : 0);
  }, [blocks, isTodoExpanded]);

  // Handle collapse all
  const handleCollapseAll = useCallback(async () => {
    // Collapse all blocks
    const expandedBlockIds = blocks.filter(b => b.isExpanded).map(b => b.id);

    // Update local state immediately
    setBlocks(prev => prev.map(b => ({ ...b, isExpanded: false })));

    // Trigger force collapse on todo
    setForceTodoCollapse(true);
    // Reset after a tick to allow re-trigger
    setTimeout(() => setForceTodoCollapse(false), 100);

    // Persist to database (batch updates)
    for (const blockId of expandedBlockIds) {
      await updateBlockMutation.mutateAsync({
        blockId,
        updates: { is_collapsed: true }
      });
    }
  }, [blocks, updateBlockMutation]);

  // Sync journal name from database
  useEffect(() => {
    if (journal) {
      setJournalName(journal.name);
    }
  }, [journal]);

  // Sync blocks from database to local state
  useEffect(() => {
    if (journalBlocks) {
      const localBlocks: Block[] = journalBlocks.map(dbBlock => {
        // Map database enum to frontend block types
        let frontendType: Block['type'] = dbBlock.block_type as Block['type'];
        if (dbBlock.block_type === 'explainer') {
          frontendType = 'slide-deck';
        }

        // Process PDF content to ensure status is correct for storage-backed files
        let processedContent: unknown = dbBlock.content;
        if (frontendType === 'pdf' && processedContent && typeof processedContent === 'object') {
          const pdfContent = processedContent as Record<string, unknown>;
          // If we have storage path, regenerate signed URL (they expire)
          if (pdfContent.storagePath) {
            processedContent = {
              ...pdfContent,
              status: 'ready',
            };
            // Regenerate signed URL in background
            const storagePath = pdfContent.storagePath as string;
            supabase.storage
              .from('original-pdfs')
              .createSignedUrl(storagePath, 3600)
              .then(({ data, error }) => {
                if (data && !error) {
                  // Update the block's storage URL with proper typing
                  setBlocks(prev => prev.map(b => {
                    if (b.id === dbBlock.id) {
                      const currentContent = b.content as PDFContent;
                      return {
                        ...b,
                        content: {
                          ...currentContent,
                          storageUrl: data.signedUrl
                        } as PDFContent
                      };
                    }
                    return b;
                  }));
                }
              });
          }
        }

        // Process content based on block type
        let content: BlockContent | StreamingNoteContent | null = processedContent as BlockContent | StreamingNoteContent | null;

        // For note blocks, handle initial empty state
        if (frontendType === 'note' && processedContent && typeof processedContent === 'object') {
          const noteContent = processedContent as Record<string, unknown>;
          // If it's our initial empty state marker, convert to undefined
          if (noteContent.blocks && Array.isArray(noteContent.blocks) && noteContent.blocks.length === 0) {
            content = null;
          }
        }

        return {
          id: dbBlock.id,
          type: frontendType,
          title: dbBlock.title,
          content: content || null,
          isExpanded: !dbBlock.is_collapsed,
          createdAt: new Date(dbBlock.created_at),
        };
      });
      setBlocks(localBlocks);
    }
  }, [journalBlocks]);

  // Track YouTube blocks currently being analyzed to prevent duplicate calls
  const analyzingYouTubeBlocksRef = useRef<Set<string>>(new Set());

  // Auto-trigger YouTube analysis for new blocks with analyzing: true
  // This handles both manual adds and AI-added blocks in one code path
  useEffect(() => {
    blocks.forEach(async (block) => {
      if (block.type !== 'youtube') return;

      const content = block.content as {
        videoUrl?: string;
        analyzing?: boolean;
        transcript?: unknown[];
        analysis?: unknown;
        error?: string;
      } | null;

      // Skip if no content or no videoUrl
      if (!content?.videoUrl) return;

      // Skip if not marked as needing analysis
      if (!content.analyzing) return;

      // Skip if already has analysis data or error
      if (content.transcript || content.analysis || content.error) return;

      // Skip if we're already analyzing this block
      if (analyzingYouTubeBlocksRef.current.has(block.id)) return;

      // Mark as in-progress
      analyzingYouTubeBlocksRef.current.add(block.id);

      console.log(`[YouTube] Auto-triggering analysis for block ${block.id}`);

      try {
        const analysis = await analyzeYouTube({
          journal_id: journalId,
          video_url: content.videoUrl,
          generate_summary: true,
        });

        console.log('[YouTube] Analysis complete:', analysis);

        // Update block with analysis results
        const contentWithAnalysis = {
          videoUrl: content.videoUrl,
          analyzing: false,
          video_id: analysis.video_id,
          transcript_available: analysis.transcript_available,
          transcript_length: analysis.transcript_length,
          transcript: analysis.transcript,
          analysis: analysis.analysis,
        };

        setBlocks(prev => prev.map(b =>
          b.id === block.id ? { ...b, content: contentWithAnalysis } : b
        ));

        await updateBlockMutation.mutateAsync({
          blockId: block.id,
          updates: { content: contentWithAnalysis }
        });

        // Trigger journal summary update
        summaryUpdates.triggerUpdate('youtube', 'add', {
          hasContent: analysis.transcript_available,
          blockId: block.id,
          isAIGenerated: false,
        });

      } catch (error) {
        console.error('[YouTube] Analysis failed:', error);
        // Mark as not analyzing on error - update both local state and DB
        const errorContent = { ...content, analyzing: false, error: 'Analysis failed' };
        setBlocks(prev => prev.map(b =>
          b.id === block.id ? { ...b, content: errorContent as BlockContent } : b
        ));
        // Also update DB
        await updateBlockMutation.mutateAsync({
          blockId: block.id,
          updates: { content: errorContent }
        }).catch(e => console.error('[YouTube] Failed to save error state:', e));
      } finally {
        // Remove from in-progress tracking
        analyzingYouTubeBlocksRef.current.delete(block.id);
      }
    });
  }, [blocks, journalId, updateBlockMutation, summaryUpdates]);

  // Track quiz blocks currently being generated to prevent duplicate calls
  const generatingQuizBlocksRef = useRef<Set<string>>(new Set());

  // Auto-trigger quiz generation for new blocks with generating: true
  // This handles AI-added quiz blocks - frontend calls the quiz generation API
  useEffect(() => {
    blocks.forEach(async (block) => {
      if (block.type !== 'quiz') return;

      const content = block.content as {
        generating?: boolean;
        prompt?: string;
        quizType?: string;
        questionCount?: number;
        difficulty?: string;
        generatedQuiz?: unknown;
        error?: string;
      } | null;

      // Skip if no content or not marked for generation
      if (!content?.generating) return;

      // Skip if already has quiz data or error
      if (content.generatedQuiz || content.error) return;

      // Skip if we're already generating this block
      if (generatingQuizBlocksRef.current.has(block.id)) return;

      // Need a prompt/topic
      if (!content.prompt) return;

      // Mark as in-progress
      generatingQuizBlocksRef.current.add(block.id);

      console.log(`[Quiz] Auto-triggering generation for block ${block.id}`);

      try {
        const response = await generateQuiz({
          journal_id: journalId,
          topic: content.prompt,
          quiz_type: (content.quizType || 'mcq') as 'mcq' | 'true_false' | 'fill_blank' | 'matching' | 'spot_mistake',
          question_count: content.questionCount || 5,
          difficulty: (content.difficulty || 'medium') as 'easy' | 'medium' | 'hard',
        });

        console.log('[Quiz] Generation complete:', response);

        // Build updated content with generated quiz
        const quizTypeLabels: Record<string, string> = {
          mcq: 'MCQ',
          true_false: 'True/False',
          fill_blank: 'Fill Blanks',
          matching: 'Matching',
          spot_mistake: 'Spot Error',
        };

        const contentWithQuiz = {
          prompt: content.prompt,
          quizType: content.quizType || 'mcq',
          questionCount: content.questionCount || 5,
          difficulty: content.difficulty || 'medium',
          generating: false,
          generatedQuiz: response.quiz,
          answers: {},
        };

        // Update local state
        setBlocks(prev => prev.map(b =>
          b.id === block.id ? { ...b, content: contentWithQuiz } : b
        ));

        // Generate title from quiz
        const typeLabel = quizTypeLabels[content.quizType || 'mcq'] || 'Quiz';
        const blockTitle = response.quiz.title
          ? `${typeLabel}: ${response.quiz.title}`
          : `${typeLabel} Quiz`;

        // Update DB
        await updateBlockMutation.mutateAsync({
          blockId: block.id,
          updates: { content: contentWithQuiz, title: blockTitle }
        });

        // Trigger journal summary update
        summaryUpdates.triggerUpdate('quiz', 'add', {
          hasContent: true,
          blockId: block.id,
          isAIGenerated: true,
        });

      } catch (error) {
        console.error('[Quiz] Generation failed:', error);
        // Mark as not generating on error
        const errorContent = { ...content, generating: false, error: 'Quiz generation failed' };
        setBlocks(prev => prev.map(b =>
          b.id === block.id ? { ...b, content: errorContent as BlockContent } : b
        ));
        // Update DB
        await updateBlockMutation.mutateAsync({
          blockId: block.id,
          updates: { content: errorContent }
        }).catch(e => console.error('[Quiz] Failed to save error state:', e));
      } finally {
        // Remove from in-progress tracking
        generatingQuizBlocksRef.current.delete(block.id);
      }
    });
  }, [blocks, journalId, updateBlockMutation, summaryUpdates]);

  // Tab state - start with home tab
  const [tabs, setTabs] = useState<Tab[]>([
    {
      id: 'home',
      title: 'Home',
      icon: LibraryBig,
      content: 'home'
    }
  ]);
  const [activeTabId, setActiveTabId] = useState('home');
  const [draggingTabId, setDraggingTabId] = useState<string | null>(null);
  const [dragOverTabId, setDragOverTabId] = useState<string | null>(null);

  // Canvas viewport ref for vision capture
  const canvasViewportRef = useRef<HTMLDivElement>(null);

  // Drag and drop sensors for blocks
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Expose the canvas viewport element getter to parent component
  useEffect(() => {
    if (onCanvasViewportRef) {
      onCanvasViewportRef(() => canvasViewportRef.current);
    }
  }, [onCanvasViewportRef]);

  // Notify parent about screen stream changes
  useEffect(() => {
    if (onScreenStreamChange) {
      onScreenStreamChange(screenStream);
    }
  }, [screenStream, onScreenStreamChange]);

  // Notify parent about content tabs state
  useEffect(() => {
    if (onTabsChange) {
      // Has content if more than just the home tab OR if there are blocks
      onTabsChange(tabs.length > 1 || blocks.length > 0);
    }
  }, [tabs, blocks, onTabsChange]);

  // Cleanup screen sharing on component unmount
  useEffect(() => {
    return () => {
      if (screenStream) {
        screenStream.getTracks().forEach(track => {
          track.stop();
        });
      }
    };
  }, [screenStream]);

  // Map frontend block types to database enum (defined outside callbacks)
  const mapBlockTypeToDb = useCallback((type: Block['type']): BlockType => {
    if (type === 'slide-deck') return 'explainer';
    if (type === 'streaming-note') return 'note';
    return type as BlockType;
  }, []);

  // Handle journal name update
  const handleJournalNameBlur = useCallback(async () => {
    setIsEditingName(false);
    if (journalName !== journal?.name) {
      await updateJournalMutation.mutateAsync({
        journalId,
        updates: { name: journalName },
      });
    }
  }, [journalName, journal?.name, journalId, updateJournalMutation]);

  // Handle journal deletion
  const handleDeleteJournal = useCallback(async () => {
    if (window.confirm("Are you sure you want to delete this journal? This action cannot be undone.")) {
      try {
        await deleteJournalMutation.mutateAsync(journalId);
        navigate("/journals");
      } catch (error) {
        console.error("Failed to delete journal:", error);
      }
    }
  }, [journalId, deleteJournalMutation, navigate]);

  // Get initial content for each block type
  const getInitialContent = useCallback((type: Block['type']): Record<string, unknown> => {
    switch (type) {
      case 'note':
        // BlockNote expects undefined for empty, but JSONB needs an object
        // We'll store a marker that NoteBlock can recognize
        return { blocks: [] };
      case 'quiz':
        return {
          questions: [],
          generationState: 'config'
        };
      case 'slide-deck':
        return {
          generationState: 'config',
          slides: []
        };
      case 'pdf':
        return {
          status: 'empty'
        };
      case 'youtube':
        // YouTube content is just a string URL, but JSONB needs object
        // We'll use null and handle it in the render
        return { videoUrl: null };
      case 'streaming-note':
        return { markdown: '' };
      default:
        return {};
    }
  }, []);

  // Block management functions
  // Calculate fractional position for new block insertion
  const calculateInsertPosition = useCallback((atIndex?: number): number => {
    if (!journalBlocks || journalBlocks.length === 0) {
      // First block
      return 1000;
    }

    const targetIndex = atIndex !== undefined ? atIndex : journalBlocks.length;

    if (targetIndex === 0) {
      // Insert at beginning - use half of first block's position
      const firstPos = journalBlocks[0].position;
      return firstPos / 2;
    } else if (targetIndex >= journalBlocks.length) {
      // Insert at end - add 1000 to last block's position
      const lastPos = journalBlocks[journalBlocks.length - 1].position;
      return lastPos + 1000;
    } else {
      // Insert in middle - average of surrounding positions
      const prevPos = journalBlocks[targetIndex - 1].position;
      const nextPos = journalBlocks[targetIndex].position;
      return (prevPos + nextPos) / 2;
    }
  }, [journalBlocks]);

  const addBlock = useCallback(async (type: Block['type'], title: string, atIndex?: number) => {
    try {
      // Calculate fractional position
      const position = calculateInsertPosition(atIndex);

      // PDF blocks should NOT be created optimistically
      // They need file selection first, so just create in DB and wait
      if (type === 'pdf') {
        const initialContent = getInitialContent(type);
        await createBlockMutation.mutateAsync({
          journal_id: journalId,
          block_type: mapBlockTypeToDb(type),
          title,
          position,
          content: initialContent,
          is_collapsed: false,
        });
        // React Query refetch will show it with file picker
        return;
      }

      // For other blocks, use optimistic updates
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const initialContent = getInitialContent(type);

      // For optimistic rendering, notes should have undefined content
      // (NoteBlock will handle the empty state)
      let optimisticContent: BlockContent | StreamingNoteContent | null = initialContent as BlockContent | StreamingNoteContent;
      if (type === 'note') {
        optimisticContent = null; // NoteBlock handles null/undefined as empty
      }

      const optimisticBlock: Block = {
        id: tempId,
        type,
        title,
        content: optimisticContent,
        isExpanded: true,
        createdAt: new Date(),
      };

      // Optimistic update: Add to local state immediately
      setBlocks(prev => {
        const newBlocks = [...prev];
        const targetIndex = atIndex !== undefined ? atIndex : newBlocks.length;
        newBlocks.splice(targetIndex, 0, optimisticBlock);
        return newBlocks;
      });

      // Create in database (background)
      const createdBlock = await createBlockMutation.mutateAsync({
        journal_id: journalId,
        block_type: mapBlockTypeToDb(type),
        title,
        position,
        content: initialContent,
        is_collapsed: false,
      });

      // Replace temp block with real block ID
      setBlocks(prev => prev.map(b =>
        b.id === tempId ? { ...b, id: createdBlock.id } : b
      ));

      console.log('Block created successfully:', createdBlock.id);
    } catch (error) {
      console.error('Error creating block:', error);

      // Rollback optimistic update on error
      setBlocks(prev => prev.filter(b => !b.id.startsWith('temp-')));

      // React Query will show toast via onError in the mutation
      throw error; // Re-throw to let error boundary catch if needed
    }
  }, [journalId, calculateInsertPosition, createBlockMutation, mapBlockTypeToDb, getInitialContent]);

  const updateBlock = useCallback(async (blockId: string, updates: Partial<Block>) => {
    // Find block type to determine update strategy
    const currentBlock = blocks.find(b => b.id === blockId);

    // Update local state immediately for responsiveness
    setBlocks(prev => prev.map(block =>
      block.id === blockId ? { ...block, ...updates } : block
    ));

    // Prepare database update for non-content fields
    const dbUpdates: Record<string, unknown> = {};
    if (updates.title !== undefined) dbUpdates.title = updates.title;
    if (updates.isExpanded !== undefined) dbUpdates.is_collapsed = !updates.isExpanded;

    // For YouTube and PDF blocks, save content immediately with title
    // This prevents race conditions where title triggers refetch before content is saved
    const needsImmediateContentSave = currentBlock?.type === 'youtube' || currentBlock?.type === 'pdf';

    if (needsImmediateContentSave && updates.content !== undefined) {
      // Save everything together immediately
      dbUpdates.content = updates.content;
      await updateBlockMutation.mutateAsync({ blockId, updates: dbUpdates });
    } else {
      // Update non-content fields immediately if present
      if (Object.keys(dbUpdates).length > 0) {
        await updateBlockMutation.mutateAsync({ blockId, updates: dbUpdates });
      }

      // For other content updates, use debounced update (separate from title/expand updates)
      if (updates.content !== undefined) {
        debouncedContentUpdate(blockId, updates.content as Record<string, unknown>);

        // Trigger debounced summary update for meaningful content edits
        if (currentBlock) {
          const dbBlockType = mapBlockTypeToDb(currentBlock.type);
          summaryUpdates.triggerUpdate(dbBlockType, 'edit', {
            hasContent: true,
            blockId
          });
        }
      }
    }
  }, [blocks, updateBlockMutation, debouncedContentUpdate, summaryUpdates, mapBlockTypeToDb]);

  const deleteBlock = useCallback(async (blockId: string) => {
    // Find the block to check if it has storage files
    const blockToDelete = blocks.find(b => b.id === blockId);

    if (!blockToDelete) return;

    // Determine if block has meaningful content
    const hasContent = (() => {
      if (blockToDelete.type === 'note') {
        const noteContent = blockToDelete.content as { markdown?: string; blocks?: unknown[] } | null;
        return Boolean(noteContent?.markdown || noteContent?.blocks?.length);
      }
      if (blockToDelete.type === 'pdf') {
        const pdfContent = blockToDelete.content as PDFContent | null;
        return Boolean(pdfContent?.storagePath);
      }
      if (blockToDelete.type === 'youtube') {
        const ytContent = blockToDelete.content as YouTubeContent | null;
        return Boolean(ytContent?.videoUrl);
      }
      if (blockToDelete.type === 'slide-deck') {
        const deckContent = blockToDelete.content as SlideDeckBlockContent | null;
        return Boolean(deckContent?.generatedSlides?.length);
      }
      if (blockToDelete.type === 'quiz') {
        const quizContent = blockToDelete.content as QuizBlockState | null;
        return Boolean(quizContent?.generatedQuiz?.items?.length);
      }
      return false;
    })();

    // Cancel any in-progress uploads
    if (pdfUploads[blockId]?.isUploading) {
      setPdfUploads(prev => {
        const { [blockId]: _, ...rest } = prev;
        return rest;
      });
    }

    // Delete from database
    await deleteBlockMutation.mutateAsync(blockId);

    // Trigger smart summary update (checks if update is needed based on block type)
    const dbBlockType = mapBlockTypeToDb(blockToDelete.type);
    summaryUpdates.triggerUpdate(dbBlockType, 'delete', {
      hasContent,
      blockId
    });

    // Clean up storage files if it's a PDF with stored content
    if (blockToDelete.type === 'pdf') {
      const pdfContent = blockToDelete.content as PDFContent | null;
      if (pdfContent?.storagePath) {
        // Delete from storage in background (don't wait)
        deletePDFFromStorage(pdfContent.storagePath).then(success => {
          if (success) {
            console.log('PDF deleted from storage:', pdfContent.storagePath);
          }
        });
      }
    }
  }, [blocks, deleteBlockMutation, pdfUploads, summaryUpdates, mapBlockTypeToDb]);

  const toggleBlockExpanded = useCallback(async (blockId: string) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;

    // Update local state immediately
    setBlocks(prev => prev.map(b =>
      b.id === blockId ? { ...b, isExpanded: !b.isExpanded } : b
    ));

    // Update database
    await updateBlockMutation.mutateAsync({
      blockId,
      updates: { is_collapsed: block.isExpanded }  // Toggle: if currently expanded, collapse it
    });
  }, [blocks, updateBlockMutation]);

  // Handle block drag end for reordering
  const handleBlockDragEnd = useCallback(async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      // Update local state immediately
      setBlocks(prev => {
        const oldIndex = prev.findIndex(b => b.id === active.id);
        const newIndex = prev.findIndex(b => b.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(prev, oldIndex, newIndex);

          // Update database with new positions
          const updates = reordered.map((block, index) => ({
            id: block.id,
            position: index,
          }));
          reorderBlocksMutation.mutate(updates);

          return reordered;
        }
        return prev;
      });
    }
  }, [reorderBlocksMutation]);

  // Handle AI ask from insert point - creates a streaming note block
  const handleAIAsk = useCallback((prompt: string, atIndex: number) => {
    const newBlock: Block = {
      id: `block-${Date.now()}`,
      type: 'streaming-note',
      title: 'AI Response',
      isExpanded: true,
      content: { prompt } as StreamingNoteContent,
      createdAt: new Date(),
    };
    setBlocks(prev => {
      if (atIndex >= 0 && atIndex <= prev.length) {
        const newBlocks = [...prev];
        newBlocks.splice(atIndex, 0, newBlock);
        return newBlocks;
      }
      return [...prev, newBlock];
    });
  }, []);

  // Handle streaming complete - replace temporary block with real database block
  const handleStreamingComplete = useCallback(async (
    blockId: string,
    streamedText: string,
    metadata?: { summary: string; key_points: string[]; related_topics: string[]; confidence: number }
  ) => {
    // Find the temporary streaming block's position
    const blockIndex = blocks.findIndex(b => b.id === blockId);
    if (blockIndex === -1) return;

    // Get the question from the streaming block before removing it
    const streamingBlock = blocks.find(b => b.id === blockId);
    const userQuestion = (streamingBlock?.content as StreamingNoteContent)?.prompt || '';

    // Remove the temporary block from local state
    setBlocks(prev => prev.filter(b => b.id !== blockId));

    // Create a real database block at the same position
    // Content includes markdown and AI metadata
    try {
      const position = calculateInsertPosition(blockIndex);

      // Build content with AI metadata
      const blockContent: Record<string, unknown> = {
        markdown: streamedText,
        user_question: userQuestion,  // Store for summary context
      };

      // Add AI metadata if available
      if (metadata) {
        blockContent.ai_summary = metadata.summary;
        blockContent.ai_key_points = metadata.key_points;
        blockContent.ai_related_topics = metadata.related_topics;
        blockContent.ai_confidence = metadata.confidence;
      }

      // Use question as title (truncated) or fallback to "Q&A"
      const blockTitle = userQuestion
        ? (userQuestion.length > 60 ? userQuestion.slice(0, 57) + '...' : userQuestion)
        : 'Q&A';

      const newBlock = await createBlockMutation.mutateAsync({
        journal_id: journalId,
        block_type: mapBlockTypeToDb('note'),
        title: blockTitle,
        content: blockContent,
        position,
        is_collapsed: false,
      });

      // Check if this is the first REAL block (streaming blocks don't count)
      const realBlockCount = blocks.filter(b => !b.id.startsWith('streaming-')).length;
      const isFirstBlock = realBlockCount === 0;

      console.log(`[Streaming Complete] Real blocks: ${realBlockCount}, isFirstBlock: ${isFirstBlock}`);

      // AI-generated notes always trigger IMMEDIATE update (high-value, complete content)
      summaryUpdates.triggerUpdate('note', 'add', {
        hasContent: true,
        blockId: newBlock.id,
        isFirstBlock,
        isAIGenerated: true,  // Always immediate for AI responses
        totalBlocks: realBlockCount + 1
      });

      // Insert the real block at the correct position
      setBlocks(prev => {
        const updated = [...prev];
        updated.splice(blockIndex, 0, {
          id: newBlock.id,
          type: 'note',
          title: newBlock.title,
          isExpanded: !newBlock.is_collapsed,
          content: newBlock.content as BlockContent,
          createdAt: new Date(newBlock.created_at),
        });
        return updated;
      });
    } catch (error) {
      console.error('Failed to create note block from streaming:', error);
      // Re-add the temporary block if DB save failed
      setBlocks(prev => {
        const updated = [...prev];
        updated.splice(blockIndex, 0, {
          id: blockId,
          type: 'note',
          title: 'AI Response',
          isExpanded: true,
          content: { markdown: streamedText },
          createdAt: new Date(),
        });
        return updated;
      });
    }
  }, [blocks, journalId, calculateInsertPosition, mapBlockTypeToDb, createBlockMutation]);

  // Handle action from empty state (AI ask)
  const handleEmptyStateAction = useCallback((cardId: string, value: string) => {
    if (cardId === 'ask' && value.trim()) {
      // Create a streaming note block at the beginning
      handleAIAsk(value.trim(), 0);
    }
  }, [handleAIAsk]);

  // Handle direct card click - all cards create blocks directly
  const handleCardClick = useCallback(async (cardId: string) => {
    try {
      switch (cardId) {
        case 'notes':
          await addBlock('note', 'New Note');
          break;
        case 'upload-pdf':
          await addBlock('pdf', 'PDF Document');
          break;
        case 'youtube-video':
          await addBlock('youtube', 'YouTube Video');
          break;
        case 'slide-deck':
          await addBlock('slide-deck', 'Explainer');
          break;
        case 'quiz':
          await addBlock('quiz', 'New Quiz');
          break;
        default:
          console.log('Unhandled card click:', cardId);
      }
    } catch (error) {
      console.error('Error handling card click:', error);
    }
  }, [addBlock]);

  // Handle insert from insert point at specific index
  const handleInsertBlock = useCallback(async (cardId: string, atIndex: number) => {
    try {
      switch (cardId) {
        case 'notes':
          await addBlock('note', 'New Note', atIndex);
          break;
        case 'upload-pdf':
          await addBlock('pdf', 'PDF Document', atIndex);
          break;
        case 'youtube-video':
          await addBlock('youtube', 'YouTube Video', atIndex);
          break;
        case 'slide-deck':
          await addBlock('slide-deck', 'Explainer', atIndex);
          break;
        case 'quiz':
          await addBlock('quiz', 'New Quiz', atIndex);
          break;
        default:
          console.log('Unhandled insert:', cardId);
      }
    } catch (error) {
      console.error('Error handling insert:', error);
    }
  }, [addBlock]);

  // Open video in a new tab
  const openVideoInTab = useCallback((videoUrl: string, title?: string) => {
    // Extract video ID for the tab
    const videoIdMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/);
    const videoId = videoIdMatch?.[1];

    if (!videoId) return;

    const newTabId = `youtube-${Date.now()}`;
    const newTab: Tab = {
      id: newTabId,
      title: title || 'YouTube Video',
      icon: IconBrandYoutube,
      content: 'youtube',
      data: {
        videoUrl,
        videoId,
        journalId,  // Pass journalId so tab can fetch transcript
      }
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);
  }, [journalId]);

  // Open PDF in a new tab
  const openPDFInTab = useCallback((pdfUrl: string, fileName?: string) => {
    const newTabId = `pdf-${Date.now()}`;
    const newTab: Tab = {
      id: newTabId,
      title: fileName || 'PDF Document',
      icon: IconFileText,
      content: 'pdf',
      data: {
        pdfUrl,
        fileName,
      }
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);
  }, []);

  // Open slide deck in a new tab
  const openSlideDeckInTab = useCallback((slides: SlideContent[], title: string) => {
    const newTabId = `slides-${Date.now()}`;
    const newTab: Tab = {
      id: newTabId,
      title: title || 'Explainer',
      icon: IconPresentation,
      content: 'slide-deck',
      data: {
        slides,
        deckTitle: title,
      }
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);
  }, []);

  const closeTab = useCallback((tabId: string) => {
    // Prevent closing the home tab
    if (tabId === 'home') return;

    setTabs(prev => prev.filter(tab => tab.id !== tabId));

    // If closing active tab, switch to another tab
    if (activeTabId === tabId) {
      setTabs(currentTabs => {
        const remainingTabs = currentTabs.filter(tab => tab.id !== tabId);
        const currentIndex = currentTabs.findIndex(tab => tab.id === tabId);
        const newActiveTab = remainingTabs[currentIndex - 1] || remainingTabs[currentIndex] || remainingTabs[0];
        setActiveTabId(newActiveTab?.id || 'home');
        return remainingTabs;
      });
    }
  }, [activeTabId]);

  // Drag & drop reordering
  const onTabDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, tabId: string) => {
    if (tabId === 'home') return;
    setDraggingTabId(tabId);
    e.dataTransfer.setData('text/tab-id', tabId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const onTabDragOver = useCallback((e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    if (!draggingTabId || targetId === 'home' || targetId === draggingTabId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTabId(targetId);
  }, [draggingTabId]);

  const onTabDragLeave = useCallback(() => {
    setDragOverTabId(null);
  }, []);

  const onTabDrop = useCallback((e: React.DragEvent<HTMLDivElement>, targetId: string) => {
    const sourceId = e.dataTransfer.getData('text/tab-id') || draggingTabId;
    setDragOverTabId(null);
    setDraggingTabId(null);
    if (!sourceId || sourceId === targetId) return;

    setTabs(currentTabs => {
      const fromIndex = currentTabs.findIndex(t => t.id === sourceId);
      const toIndex = currentTabs.findIndex(t => t.id === targetId);
      if (fromIndex <= 0 || toIndex < 0) return currentTabs;

      const insertIndex = toIndex === 0 ? 1 : toIndex;
      const newTabs = [...currentTabs];
      const [moved] = newTabs.splice(fromIndex, 1);
      newTabs.splice(insertIndex, 0, moved);
      return newTabs;
    });
  }, [draggingTabId]);

  // Render block content based on type
  const renderBlockContent = (block: Block) => {
    switch (block.type) {
      case 'note': {
        // Sanitize note content - handle our empty marker
        let noteContent: NoteContent | undefined = block.content as NoteContent | undefined;
        if (noteContent && typeof noteContent === 'object' && !Array.isArray(noteContent)) {
          const obj = noteContent as Record<string, unknown>;
          // If it's our empty marker, convert to undefined
          if ('blocks' in obj && Array.isArray(obj.blocks) && obj.blocks.length === 0) {
            noteContent = undefined;
          }
        }

        return (
          <NoteBlock
            initialContent={noteContent}
            isExpanded={block.isExpanded}
            onChange={(content) => updateBlock(block.id, { content })}
            onToggle={() => toggleBlockExpanded(block.id)}
            onDelete={() => deleteBlock(block.id)}
          />
        );
      }
      case 'pdf': {
        const pdfContent = block.content as PDFContent | null;
        const uploadState = pdfUploads[block.id];
        const isCloudSynced = !!pdfContent?.storagePath && !uploadState?.isUploading;

        return (
          <PDFBlock
            // PDFBlock manages its own blob URL internally for instant display
            // This is just the storage URL for when user returns after refresh
            pdfUrl={pdfContent?.storageUrl}
            fileName={pdfContent?.fileName}
            content={pdfContent || undefined}
            isUploading={uploadState?.isUploading}
            uploadProgress={uploadState?.progress}
            uploadError={uploadState?.error}
            isCloudSynced={isCloudSynced}
            onFileSelect={async (file, blobUrl) => {
              console.log('PDF selected:', file.name);

              // Update block title immediately
              setBlocks(prev => prev.map(b =>
                b.id === block.id
                  ? { ...b, title: file.name }
                  : b
              ));

              // Start tracking upload
              setPdfUploads(prev => ({
                ...prev,
                [block.id]: { isUploading: true, progress: 0 }
              }));

              // Start background upload
              if (user?.id) {
                try {
                  const fileExt = file.name.split('.').pop() || 'pdf';
                  const storagePath = `${user.id}/${journalId}/${block.id}.${fileExt}`;

                  console.log('Uploading PDF to:', storagePath);

                  const { error } = await supabase.storage
                    .from('original-pdfs')
                    .upload(storagePath, file, {
                      cacheControl: '3600',
                      upsert: true,
                    });

                  if (error) throw error;

                  // Get SIGNED URL for better streaming/range request support
                  const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                    .from('original-pdfs')
                    .createSignedUrl(storagePath, 3600); // 1 hour expiry

                  if (signedUrlError) throw signedUrlError;

                  const storageUrl = signedUrlData.signedUrl;
                  console.log('PDF signed URL:', storageUrl);

                  // Update database with storage path (mark as analyzing)
                  await updateBlockMutation.mutateAsync({
                    blockId: block.id,
                    updates: {
                      title: file.name,
                      content: {
                        status: 'processing',
                        fileName: file.name,
                        fileSize: file.size,
                        storagePath,
                        storageUrl,
                        analyzing: true,
                      }
                    }
                  });

                  // Clear upload tracking
                  setPdfUploads(prev => ({
                    ...prev,
                    [block.id]: { isUploading: false, progress: 100 }
                  }));

                  console.log('PDF uploaded successfully, starting analysis:', storagePath);

                  // Trigger PDF analysis in background
                  try {
                    const analysisResult = await analyzePDF({
                      journal_id: journalId,
                      block_id: block.id,
                      storage_path: storagePath,
                      generate_summary: true,
                    });

                    if (analysisResult.success) {
                      console.log('PDF analysis complete:', analysisResult.title);

                      // Update block with analysis results
                      await updateBlockMutation.mutateAsync({
                        blockId: block.id,
                        updates: {
                          content: {
                            status: 'ready',
                            fileName: file.name,
                            fileSize: file.size,
                            storagePath,
                            storageUrl,
                            analyzing: false,
                            analysis: analysisResult.analysis,
                            pdf_context: analysisResult.pdf_context,
                            text_assets: analysisResult.text_assets,
                            processing: analysisResult.processing,
                          }
                        }
                      });

                      // Trigger journal summary update (force immediate since analysis is complete)
                      summaryUpdates.triggerUpdate('pdf', 'add', {
                        hasContent: true,
                        blockId: block.id,
                        isFirstBlock: blocks.length === 1,
                        totalBlocks: blocks.length,
                        forceImmediate: true,  // Bypass skip since PDF is now analyzed
                      });
                    } else {
                      console.error('PDF analysis failed:', analysisResult.error);
                      // Update block with error but keep content accessible
                      await updateBlockMutation.mutateAsync({
                        blockId: block.id,
                        updates: {
                          content: {
                            status: 'ready',
                            fileName: file.name,
                            fileSize: file.size,
                            storagePath,
                            storageUrl,
                            analyzing: false,
                            error: analysisResult.error,
                          }
                        }
                      });
                    }
                  } catch (analysisError) {
                    console.error('PDF analysis request failed:', analysisError);
                    await updateBlockMutation.mutateAsync({
                      blockId: block.id,
                      updates: {
                        content: {
                          status: 'ready',
                          fileName: file.name,
                          fileSize: file.size,
                          storagePath,
                          storageUrl,
                          analyzing: false,
                          error: String(analysisError),
                        }
                      }
                    });
                  }
                } catch (error: unknown) {
                  console.error('PDF upload failed:', error);
                  const errorMessage = error instanceof Error ? error.message : 'Upload failed';

                  setPdfUploads(prev => ({
                    ...prev,
                    [block.id]: { isUploading: false, progress: 0, error: errorMessage }
                  }));
                }
              }
            }}
            onOpenInTab={() => {
              // PDFBlock will provide the URL via its internal state or prop
              const url = pdfContent?.storageUrl;
              if (url) {
                openPDFInTab(url, pdfContent?.fileName);
              }
            }}
          />
        );
      }
      case 'youtube': {
        // YouTube content with full analysis, transcript, etc.
        const youtubeContent = block.content as {
          videoUrl?: string | null;
          analyzing?: boolean;
          video_id?: string;
          transcript_available?: boolean;
          transcript_length?: number;
          transcript?: Array<{ text: string; start: number; duration: number }>;
          analysis?: {
            summary: string;
            key_points: string[];
            topics: string[];
            learning_objectives: string[];
          };
          error?: string;
        } | string | null;

        let videoUrl: string | undefined;

        if (typeof youtubeContent === 'string') {
          // Legacy format: plain string
          videoUrl = youtubeContent;
        } else if (youtubeContent && typeof youtubeContent === 'object' && 'videoUrl' in youtubeContent) {
          // New format: object with videoUrl property
          videoUrl = youtubeContent.videoUrl || undefined;
        }

        return (
          <YouTubeBlock
            videoUrl={videoUrl}
            content={typeof youtubeContent === 'object' ? youtubeContent : undefined}
            onSubmit={async (url) => {
              console.log('[YouTube] URL submitted:', url);

              // Extract video ID
              const videoIdMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&\n?#]+)/);
              const videoId = videoIdMatch?.[1];

              // Fetch video title using YouTube oEmbed API (no API key needed)
              let videoTitle = 'YouTube Video';
              if (videoId) {
                try {
                  const oembedUrl = `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`;
                  const response = await fetch(oembedUrl);
                  if (response.ok) {
                    const data = await response.json();
                    videoTitle = data.title || 'YouTube Video';
                  }
                } catch (error) {
                  console.warn('[YouTube] Failed to fetch title:', error);
                }
              }

              // 1. Update block immediately with URL (so user sees video right away)
              setBlocks(prev => prev.map(b =>
                b.id === block.id ? {
                  ...b,
                  content: { videoUrl: url, analyzing: true },
                  title: videoTitle
                } : b
              ));

              await updateBlockMutation.mutateAsync({
                blockId: block.id,
                updates: {
                  title: videoTitle,
                  content: { videoUrl: url, analyzing: true }
                }
              });

              // 2. Analyze video in background (transcript + AI summary)
              console.log('[YouTube] Starting analysis...');
              try {
                const analysis = await analyzeYouTube({
                  journal_id: journalId,
                  video_url: url,
                  generate_summary: true,
                });

                console.log('[YouTube] Analysis complete:', analysis);

                // 3. Update block with analysis results (including transcript for tab view)
                const contentWithAnalysis = {
                  videoUrl: url,
                  analyzing: false,
                  video_id: analysis.video_id,
                  transcript_available: analysis.transcript_available,
                  transcript_length: analysis.transcript_length,
                  transcript: analysis.transcript,  // Store timestamped segments
                  analysis: analysis.analysis,
                };

                setBlocks(prev => prev.map(b =>
                  b.id === block.id ? { ...b, content: contentWithAnalysis } : b
                ));

                await updateBlockMutation.mutateAsync({
                  blockId: block.id,
                  updates: { content: contentWithAnalysis }
                });

                // 4. Trigger journal summary update (YouTube is high-value content)
                summaryUpdates.triggerUpdate('youtube', 'add', {
                  hasContent: analysis.transcript_available,
                  blockId: block.id,
                  isAIGenerated: false,  // Not AI-generated, but important content
                });

              } catch (error) {
                console.error('[YouTube] Analysis failed:', error);
                // Still save the URL, just without analysis
                setBlocks(prev => prev.map(b =>
                  b.id === block.id ? {
                    ...b,
                    content: { videoUrl: url, analyzing: false, error: 'Analysis failed' }
                  } : b
                ));
              }
            }}
            onRemove={() => {
              updateBlock(block.id, { content: { videoUrl: null }, title: 'YouTube Video' });
            }}
            onOpenInTab={() => {
              if (videoUrl) {
                openVideoInTab(videoUrl, block.title);
              }
            }}
          />
        );
      }
      case 'slide-deck': {
        const slideDeckContent = block.content as SlideDeckBlockContent | null;
        return (
          <SlideDeckBlock
            journalId={journalId}
            blockId={block.id}
            initialState={slideDeckContent || undefined}
            onStateChange={(state) => {
              updateBlock(block.id, { content: state });
            }}
            onTitleChange={(title) => {
              updateBlock(block.id, { title });
            }}
            onOpenInTab={(slides, title) => {
              openSlideDeckInTab(slides, title);
            }}
          />
        );
      }
      case 'quiz': {
        const quizContent = block.content as QuizBlockState | null;
        return (
          <QuizBlock
            journalId={journalId}
            initialState={quizContent || undefined}
            onStateChange={(state) => {
              updateBlock(block.id, { content: state });
            }}
            onTitleChange={(title) => {
              updateBlock(block.id, { title });
            }}
          />
        );
      }
      case 'streaming-note': {
        const streamingContent = block.content as StreamingNoteContent;
        return (
          <StreamingNoteBlock
            prompt={streamingContent.prompt}
            journalId={journalId}
            onStreamComplete={(text, metadata) => handleStreamingComplete(block.id, text, metadata)}
            onDelete={() => deleteBlock(block.id)}
          />
        );
      }
      default:
        return <div className="py-4 text-sm text-slate-400">Unknown block type</div>;
    }
  };

  // Get icon for block type
  const getBlockIcon = (type: Block['type']) => {
    switch (type) {
      case 'note':
        return <IconFileText className="w-4 h-4 text-stone-500" />;
      case 'streaming-note':
        return <IconFileText className="w-4 h-4 text-emerald-500" />;
      case 'pdf':
        return <IconUpload className="w-4 h-4 text-slate-500" />;
      case 'youtube':
        return <IconBrandYoutube className="w-4 h-4 text-red-500" />;
      case 'slide-deck':
        return <IconPresentation className="w-4 h-4 text-amber-500" />;
      case 'quiz':
        return <IconQuestionMark className="w-4 h-4 text-emerald-500" />;
      default:
        return null;
    }
  };

  const activeTab = tabs.find(tab => tab.id === activeTabId) || tabs[0];
  const hasBlocks = blocks.length > 0;

  return (
    <div ref={canvasViewportRef} className="relative flex flex-col h-full min-h-0 transition-all duration-300 overflow-hidden">
      {/* Header with tabs */}
      <SpaceTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        dragOverTabId={dragOverTabId}
        journalName={journalName}
        onTabClick={setActiveTabId}
        onTabClose={closeTab}
        onTabDragStart={onTabDragStart}
        onTabDragOver={onTabDragOver}
        onTabDragLeave={onTabDragLeave}
        onTabDrop={onTabDrop}
        isFullscreen={isFullscreen}
        onToggleFullscreen={onToggleFullscreen}
      />

      {/* Tab Content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div ref={scrollContainerRef} className="h-full overflow-auto">
          {activeTab?.content === 'home' ? (
            /* Home Tab - Always show header and todo list */
            <div className="px-4 md:px-6 pt-16 pb-6 max-w-5xl mx-auto w-full">
              {/* Editable Journal Name Header with Share Button */}
              <div className="mb-8">
                <div className="flex items-center justify-between gap-4">
                  {isEditingName ? (
                    <input
                      type="text"
                      value={journalName}
                      onChange={(e) => setJournalName(e.target.value)}
                      onBlur={handleJournalNameBlur}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleJournalNameBlur();
                        if (e.key === 'Escape') {
                          setIsEditingName(false);
                          setJournalName(journal?.name || 'Untitled Journal');
                        }
                      }}
                      autoFocus
                      className="text-3xl font-bold text-slate-800 bg-transparent border-b-2 border-amber-500 outline-none flex-1"
                    />
                  ) : (
                    <h1
                      onClick={() => setIsEditingName(true)}
                      className="text-3xl font-bold text-slate-800 cursor-pointer hover:text-slate-600 transition-colors flex-1"
                      title="Click to edit journal name"
                    >
                      {journalName}
                    </h1>
                  )}

                  {/* Actions Area: Share + Settings */}
                  <div className="flex items-center gap-2">
                    {/* Share Button */}
                    <button
                      onClick={() => setIsShareBannerOpen(!isShareBannerOpen)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isShareBannerOpen
                        ? 'bg-slate-200 text-slate-700'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        } ${(journal as any)?.sharing_mode === 'community' ? 'text-green-600' : ''}`}
                      title="Share this journal"
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="hidden sm:inline">
                        {(journal as any)?.sharing_mode === 'community' ? 'Shared' : 'Share'}
                      </span>
                    </button>

                    {/* Settings Menu */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          className="p-2 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
                          title="Journal settings"
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          onClick={handleDeleteJournal}
                          className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete Journal
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Share Drawer */}
                <ShareDrawer
                  open={isShareBannerOpen}
                  onOpenChange={setIsShareBannerOpen}
                  journalId={journalId}
                  journalName={journalName}
                  journalSummary={journal?.summary || undefined}
                  currentSharingMode={(journal as any)?.sharing_mode}
                  currentTags={(journal as any)?.tags || []}
                  currentDescription={(journal as any)?.community_description || ''}
                  currentCoverUrl={(journal as any)?.community_cover_url || ''}
                  viewCount={(journal as any)?.view_count || 0}
                  copyCount={(journal as any)?.copy_count || 0}
                />
              </div>

              {/* Pinned Todo List - wrapped for TOC scroll targeting */}
              <div data-toc-id="todo-list" style={{ scrollMarginTop: '80px' }}>
                <JournalTodoList
                  journalId={journalId}
                  initialTodos={(journal?.todo_items as unknown as TodoItem[] | null) || []}
                  onExpandedChange={setIsTodoExpanded}
                  forceCollapse={forceTodoCollapse}
                />
              </div>

              {/* Blocks Section - Shows blocks or empty state */}
              {hasBlocks ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleBlockDragEnd}
                >
                  {/* Insert point before first block */}
                  <SpaceInsertPoint
                    onInsert={(cardId) => handleInsertBlock(cardId, 0)}
                    onAIAsk={(prompt) => handleAIAsk(prompt, 0)}
                  />

                  <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                    {blocks.map((block, index) => (
                      <div key={block.id} data-toc-id={block.id} style={{ scrollMarginTop: '80px' }}>
                        {block.type === 'note' ? (
                          /* Notes: Minimal inline style, no header */
                          <SortableBlockWrapper id={block.id}>
                            <NoteBlock
                              initialContent={block.content as NoteContent | undefined}
                              isExpanded={block.isExpanded}
                              onChange={(content) => updateBlock(block.id, { content })}
                              onToggle={() => toggleBlockExpanded(block.id)}
                              onDelete={() => deleteBlock(block.id)}
                            />
                          </SortableBlockWrapper>
                        ) : block.type === 'streaming-note' ? (
                          /* Streaming notes: Inline style like notes */
                          <SortableBlockWrapper id={block.id}>
                            {renderBlockContent(block)}
                          </SortableBlockWrapper>
                        ) : (
                          /* Other blocks: Full card style with header */
                          <SortableBlockWrapper id={block.id}>
                            <SpaceBlock
                              id={block.id}
                              title={block.title}
                              icon={getBlockIcon(block.type)}
                              isExpanded={block.isExpanded}
                              createdAt={block.createdAt}
                              onToggle={() => toggleBlockExpanded(block.id)}
                              onTitleChange={(title) => updateBlock(block.id, { title })}
                              onDelete={() => deleteBlock(block.id)}
                              onOpenInTab={
                                block.type === 'youtube' && block.content
                                  ? () => {
                                    // Extract videoUrl from content object
                                    const youtubeContent = block.content as YouTubeContent | string;
                                    const url = typeof youtubeContent === 'string'
                                      ? youtubeContent
                                      : youtubeContent?.videoUrl;
                                    if (url) {
                                      openVideoInTab(url, block.title);
                                    }
                                  }
                                  : block.type === 'pdf' && (block.content as PDFContent | null)?.storageUrl
                                    ? () => {
                                      const pdfContent = block.content as PDFContent | null;
                                      if (pdfContent?.storageUrl) {
                                        openPDFInTab(pdfContent.storageUrl, pdfContent.fileName);
                                      }
                                    }
                                    : undefined
                              }
                            >
                              {renderBlockContent(block)}
                            </SpaceBlock>
                          </SortableBlockWrapper>
                        )}

                        {/* Insert point after each block */}
                        <SpaceInsertPoint
                          onInsert={(cardId) => handleInsertBlock(cardId, index + 1)}
                          onAIAsk={(prompt) => handleAIAsk(prompt, index + 1)}
                        />
                      </div>
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                /* No blocks - just show insert point */
                <SpaceInsertPoint
                  onInsert={(cardId) => handleInsertBlock(cardId, 0)}
                  onAIAsk={(prompt) => handleAIAsk(prompt, 0)}
                  autoOpen={true}
                />
              )}

              {/* Bottom padding to allow last block to scroll to top */}
              <div className="h-[60vh]" aria-hidden="true" />
            </div>
          ) : activeTab?.content === 'youtube' && activeTab.data?.videoId ? (
            <YouTubeTabView
              videoId={activeTab.data.videoId}
              videoUrl={activeTab.data.videoUrl}
              journalId={activeTab.data.journalId}
              title={activeTab.title}
            />
          ) : activeTab?.content === 'pdf' && activeTab.data?.pdfUrl ? (
            <PDFTabView
              pdfUrl={activeTab.data.pdfUrl}
              fileName={activeTab.data.fileName}
            />
          ) : activeTab?.content === 'slide-deck' && activeTab.data?.slides ? (
            <SlideDeckTabView
              slides={activeTab.data.slides}
              title={activeTab.data.deckTitle || activeTab.title}
            />
          ) : activeTab?.content === 'custom' ? (
            /* Custom Tab - Empty for now */
            <div className="h-full flex items-center justify-center p-8">
              <div className="text-center space-y-2">
                <p className="text-sm text-slate-400">
                  Tab: {activeTab.title}
                </p>
                <p className="text-xs text-slate-300">
                  Content area ready for development
                </p>
              </div>
            </div>
          ) : (
            /* Fallback */
            <div className="h-full flex items-center justify-center">
              <div className="text-gray-400 text-xs">No content loaded</div>
            </div>
          )}
        </div>
      </div>

      {/* Journal Table of Contents - desktop only, shows when 3+ blocks */}
      {activeTab?.content === 'home' && (
        <JournalTableOfContents
          items={tocItems}
          activeItemId={activeBlockId}
          onItemClick={handleTOCItemClick}
          minItems={3}
        />
      )}

      {/* Floating Actions - Collapse All + Back to Top */}
      {activeTab?.content === 'home' && (
        <JournalFloatingActions
          expandedCount={expandedBlockCount}
          onCollapseAll={handleCollapseAll}
          scrollContainerRef={scrollContainerRef}
        />
      )}

      {/* Ambient Wave - positioned at bottom of canvas section */}
      <AmbientWave state={ambientState} audioManager={audioManager} />

      {/* Morphing Pill - positioned at bottom center of canvas section */}
      {pillProps && <MorphingPill {...pillProps} />}
    </div>
  );
};

export default SpaceCanvasSection;
